import type { RequestPayload } from '@inertiajs/core';
import { Head, router, usePoll } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import {
  ExternalLink,
  Music,
  Pause,
  Play,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import type { AdminMusicShow, MusicConsoleTrack, SharedProps } from '@/types';

type PlaybackCommand = 'play' | 'pause' | 'next' | 'previous';

// The OAuth callback lands here with its outcome in the query string.
const SPOTIFY_ERRORS: Record<string, string> = {
  token_exchange_failed: 'Spotify rejected the authorization code.',
  no_refresh_token: 'Spotify did not return a refresh token.',
  unknown: 'Something went wrong connecting Spotify.',
};

function formatMs(ms: number) {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

// Mutations answer with a flash; the toggle and the buttons both report it the same way.
function submit(
  method: 'post' | 'patch',
  url: string,
  data: RequestPayload,
  onFinish: () => void,
) {
  router.visit(url, {
    method,
    data,
    preserveScroll: true,
    onSuccess: (page) => {
      const { flash } = page.props as unknown as SharedProps;
      if (flash.alert) toast.error(flash.alert);
      else if (flash.notice) toast.success(flash.notice);
    },
    onError: () => toast.error('Request failed'),
    onFinish,
  });
}

export default function Show({
  title,
  eventSlug,
  eventName,
  spotifyConnectUrl,
  status,
}: AdminMusicShow) {
  const baseUrl = `/admin/${eventSlug}/music`;
  const [pending, setPending] = useState(false);

  // The player lives on Spotify; keep the page roughly in step with it.
  usePoll(10000, { only: ['status'] });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('spotify_success')) toast.success('Spotify connected');
    const error = params.get('spotify_error');
    if (error) toast.error(SPOTIFY_ERRORS[error] ?? `Spotify: ${error}`);
    if (params.has('spotify_success') || params.has('spotify_error')) {
      router.replace({ url: baseUrl, preserveState: true });
    }
  }, [baseUrl]);

  const playback = (command: PlaybackCommand) => {
    setPending(true);
    submit('post', `${baseUrl}/playback/${command}`, {}, () => setPending(false));
  };

  const toggleBot = (enabled: boolean) => {
    setPending(true);
    submit('patch', `${baseUrl}/bot`, { enabled }, () => setPending(false));
  };

  const { player } = status;
  const spotifyHealthy = status.spotifyConnected && !status.spotifyError;
  const botRunning =
    status.botEnabled &&
    status.musicChannelConfigured &&
    spotifyHealthy &&
    status.missingSettings.length === 0;

  return (
    <>
      <Head title={title} />
      <div className="container mx-auto flex flex-col gap-6 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">Music Bot</h1>
            <p className="text-muted-foreground">
              The Discord music bot of {eventName}: Spotify, playback and the
              tracks it announced.
            </p>
          </div>
          {status.discordChannelUrl && (
            <Button asChild variant="outline">
              <a
                href={status.discordChannelUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="h-4 w-4" />
                Open Discord channel
              </a>
            </Button>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                Bot
                <Badge variant={botRunning ? 'default' : 'secondary'}>
                  {botRunning ? 'Running' : 'Not running'}
                </Badge>
              </CardTitle>
              <CardDescription>
                Announces each track in Discord, takes votes and skips songs
                the room votes down.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <label className="flex items-center justify-between gap-3 border p-3">
                <span className="flex flex-col">
                  <span className="font-medium text-sm">Bot enabled</span>
                  <span className="text-muted-foreground text-xs">
                    Off stops announcements, votes, slash commands and song
                    requests from the portal.
                  </span>
                </span>
                <Switch
                  checked={status.botEnabled}
                  disabled={pending}
                  onCheckedChange={toggleBot}
                />
              </label>
              {!status.musicChannelConfigured && (
                <p className="text-destructive text-sm">
                  No Discord music channel set — add one in the event settings.
                </p>
              )}
              {status.missingSettings.length > 0 && (
                <p className="text-destructive text-sm">
                  Missing server settings: {status.missingSettings.join(', ')}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                Spotify
                <Badge variant={spotifyHealthy ? 'default' : 'destructive'}>
                  {spotifyHealthy ? 'Connected' : 'Not connected'}
                </Badge>
              </CardTitle>
              <CardDescription>
                {status.spotifyError
                  ? `Spotify rejected the stored token: ${status.spotifyError}`
                  : status.spotifyConnected
                    ? (player?.deviceName ?? 'No active device')
                    : 'Authorize the Spotify account the bot plays from.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {spotifyHealthy && (
                <>
                  <div className="flex items-center gap-3">
                    {player?.albumArt ? (
                      <img
                        src={player.albumArt}
                        alt=""
                        className="h-14 w-14 shrink-0 object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center bg-muted">
                        <Music className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0">
                      {player?.trackName ? (
                        <>
                          <a
                            href={player.trackUrl ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="block truncate font-medium hover:underline"
                          >
                            {player.trackName}
                          </a>
                          <p className="truncate text-muted-foreground text-sm">
                            {player.trackArtists}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {player.playing ? 'Playing' : 'Paused'} ·{' '}
                            {formatMs(player.progressMs)}
                            {player.durationMs
                              ? ` / ${formatMs(player.durationMs)}`
                              : ''}
                          </p>
                        </>
                      ) : (
                        <p className="text-muted-foreground text-sm">
                          Nothing playing
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={pending}
                      onClick={() => playback('previous')}
                      aria-label="Previous"
                    >
                      <SkipBack className="h-4 w-4" />
                    </Button>
                    {player?.playing ? (
                      <Button
                        size="icon"
                        disabled={pending}
                        onClick={() => playback('pause')}
                        aria-label="Pause"
                      >
                        <Pause className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="icon"
                        disabled={pending}
                        onClick={() => playback('play')}
                        aria-label="Play"
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={pending}
                      onClick={() => playback('next')}
                      aria-label="Next"
                    >
                      <SkipForward className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
              <Button asChild variant={spotifyHealthy ? 'outline' : 'default'}>
                <a href={spotifyConnectUrl}>
                  {status.spotifyConnected
                    ? 'Reconnect Spotify'
                    : 'Connect Spotify'}
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recent tracks</CardTitle>
            <CardDescription>
              What the bot announced, newest first, with the room's final vote.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {status.nowPlaying && (
              <TrackRow track={status.nowPlaying} current />
            )}
            {status.history.map((track) => (
              <TrackRow key={track.id} track={track} />
            ))}
            {!status.nowPlaying && status.history.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No tracks yet.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function TrackRow({
  track,
  current = false,
}: {
  track: MusicConsoleTrack;
  current?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border p-2">
      {track.albumArt ? (
        <img
          src={track.albumArt}
          alt=""
          className="h-10 w-10 shrink-0 object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-muted">
          <Music className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <a
          href={track.trackUrl}
          target="_blank"
          rel="noreferrer"
          className="block truncate font-medium text-sm hover:underline"
        >
          {track.trackName}
        </a>
        <p className="truncate text-muted-foreground text-xs">
          {track.trackArtists}
          {track.addedBy ? ` · added by ${track.addedBy}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <div className="flex gap-1">
          {current && <Badge>Now playing</Badge>}
          {track.skipped && <Badge variant="destructive">Skipped</Badge>}
          {track.voteScore !== null && (
            <Badge variant="outline">
              {track.voteScore > 0 ? '+' : ''}
              {track.voteScore} · {track.voterCount} votes
            </Badge>
          )}
        </div>
        <span className="text-muted-foreground text-xs">
          {formatDistanceToNow(new Date(track.playedAt), { addSuffix: true })}
        </span>
      </div>
    </div>
  );
}
